<script setup lang="ts">
import SearchHighlightText from '@/components/createdUi/SearchHighlightText.vue'

defineProps<{
  name: string
  count: number
  highlight?: string
  selected?: boolean
  editing?: boolean
  draft?: string
  /** Отступ строки, как paddingLeft у папки в библиотеке знаний */
  indent?: number
  menu?: 'rename' | 'edit' | 'none'
  draggable?: boolean
  dropEnabled?: boolean
  dragOver?: boolean
}>()

const emit = defineEmits<{
  select: []
  'update:draft': [value: string]
  save: []
  action: []
  remove: []
  dragstart: [event: DragEvent]
  dragover: [event: DragEvent]
  drop: [event: DragEvent]
}>()

const menuOpen = ref(false)

function onDragStart(event: DragEvent) {
  document.body.classList.add('staff-dnd-grabbing')
  emit('dragstart', event)
}

function onDragEnd() {
  document.body.classList.remove('staff-dnd-grabbing')
}

onBeforeUnmount(() => {
  document.body.classList.remove('staff-dnd-grabbing')
})
</script>

<template>
  <div class="d-flex flex-column w-100 min-w-0 ga-2">
    <div
      class="staff-entity-row"
      :class="{
        'staff-entity-row--selected': selected || editing,
        'staff-entity-row--over': dragOver,
        'staff-entity-row--menu': menuOpen,
        'staff-entity-row--draggable': draggable,
        'staff-entity-row--no-menu': menu === 'none',
      }"
      :style="indent ? { paddingInlineStart: `${indent}px` } : undefined"
      :draggable="draggable"
      @click="emit('select')"
      @dragstart="onDragStart"
      @dragend="onDragEnd"
      @dragover.prevent="dropEnabled && emit('dragover', $event)"
      @drop.prevent="dropEnabled && emit('drop', $event)"
    >
      <slot name="prepend" />
      <VTextField
        v-if="editing"
        :model-value="draft"
        density="compact"
        hide-details
        variant="plain"
        autofocus
        class="staff-entity-row__name"
        @click.stop
        @update:model-value="emit('update:draft', String($event ?? ''))"
        @keydown.enter.prevent="emit('save')"
      />
      <SearchHighlightText
        v-else
        class="staff-entity-row__name body-1"
        :text="name"
        :query="highlight"
      />

      <div v-if="!editing" class="staff-entity-row__end">
        <span class="staff-entity-row__count">{{ count }}</span>
        <VMenu
          v-if="menu !== 'none'"
          v-model="menuOpen"
          location="bottom end"
        >
          <template #activator="{ props: menuProps }">
            <VIcon
              v-bind="menuProps"
              icon="ri-more-2-line"
              size="18"
              class="staff-entity-row__more"
              @click.stop
            />
          </template>
        <VList density="compact" min-width="180">
          <VListItem
            :prepend-icon="menu === 'rename' ? 'ri-edit-line' : 'ri-pencil-line'"
            :title="menu === 'rename' ? 'Переименовать' : 'Редактировать'"
            @click="emit('action')"
          />
          <VListItem
            prepend-icon="ri-delete-bin-line"
            title="Удалить"
            @click="emit('remove')"
          />
        </VList>
        </VMenu>
      </div>
    </div>
    <VBtn
      v-if="editing"
      block
      size="small"
      color="primary"
      variant="tonal"
      prepend-icon="ri-check-line"
      @click.stop="emit('save')"
    >
      Сохранить
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
:global(body.staff-dnd-grabbing),
:global(body.staff-dnd-grabbing *) {
  cursor: grabbing !important;
}

.staff-entity-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  min-inline-size: 0;
  padding: 8px 12px;
  cursor: pointer;

  &--draggable {
    cursor: grab;
  }

  &--selected::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    inset-block: 0;
    inline-size: 3px;
    background: rgb(var(--v-theme-primary));
  }

  &--selected &__name,
  &--selected :deep(.staff-tree-icon) {
    color: rgb(var(--v-theme-primary));
  }

  &--over {
    outline: 1px dashed rgb(var(--v-theme-primary));
  }
}

.staff-entity-row__name {
  flex: 1;
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.staff-entity-row__end {
  position: relative;
  flex: 0 0 auto;
  min-inline-size: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.staff-entity-row__count {
  min-inline-size: 28px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.12);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
}

.staff-entity-row--selected .staff-entity-row__count {
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.16);
}

.staff-entity-row__more {
  display: none !important;
}

.staff-entity-row:not(.staff-entity-row--no-menu):hover .staff-entity-row__count,
.staff-entity-row--menu .staff-entity-row__count {
  display: none;
}

.staff-entity-row:not(.staff-entity-row--no-menu):hover .staff-entity-row__more,
.staff-entity-row--menu .staff-entity-row__more {
  display: inline-flex !important;
}
</style>
